import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { RequestedRoute } from '../../core/security/state/authState.model';

/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
export class DashGisUtil {
    private static cloneDepth = 0;
    private static readonly MAX_CLONE_DEPTH = 15;

    static hash(input: string, seed = 0): string {
        /* eslint-disable no-bitwise */
        let h1 = 0xdeadbeef ^ seed,
            h2 = 0x41c6ce57 ^ seed;
        for (let i = 0, ch; i < input.length; i++) {
            ch = input.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
        // return 4294967296 * (2097151 & h2) + (h1 >>> 0);
        return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
        /* eslint-enable no-bitwise */
    }

    static isObject(val) {
        return val !== null && typeof val === 'object';
    }

    static isNullOrEmpty(val: any) {
        return (
            val === undefined ||
            val === null ||
            Number.isNaN(val) ||
            (typeof val === 'object' && Object.keys(val).length === 0) ||
            (typeof val === 'string' && val.trim().length === 0)
        );
    }

    /**
     * Use JSON.parse to get a boolean value out of a string.
     * Non-parseable input returns false!
     * @param val
     */
    static parseBoolean(val: string): boolean {
        try {
            return JSON.parse(val);
        } catch {
            return false;
        }
    }

    /**
     * Parses a number by parsing to an int first and verifying if it matches the input,
     * and if not, parsing it as a float.
     * @param val
     * @return number|false
     */
    static parseNumber(val: string): number {
        const test = parseInt(val, 10);
        if (test.toString(10) === val) {
            return test;
        }
        return parseFloat(val);
    }

    /**
     * Moderately robust utility to make a deep copy of an arbitrary object.
     * Limits to 15 nested levels before falling back to shallow copy, when it
     * will issue a warning
     * @param {T} obj
     * @returns {T}
     */
    static clone<T>(obj: T): T {
        // keep track of call depth
        DashGisUtil.cloneDepth++;
        let copy;

        if (DashGisUtil.cloneDepth > DashGisUtil.MAX_CLONE_DEPTH) {
            // warn, return shallow copy
            console.warn('DashGisUtil.clone exceeded stack limit: reverting to shallow copy');
            copy = Object.assign({}, obj);
            return DashGisUtil.doReturn(copy);
        }

        if (
            null === obj ||
            'number' === typeof obj ||
            'boolean' === typeof obj ||
            'string' === typeof obj ||
            'function' === typeof obj ||
            'undefined' === typeof obj
        ) {
            return DashGisUtil.doReturn(obj);
        }

        if (obj instanceof Date) {
            copy = new Date();
            copy.setTime(obj.getTime());
            return DashGisUtil.doReturn(copy);
        }

        if (obj instanceof Array) {
            copy = [];
            for (let i = 0, len = obj.length; i < len; i++) {
                copy[i] = DashGisUtil.clone(obj[i]);
            }
            return DashGisUtil.doReturn(copy);
        }

        if (obj instanceof Object) {
            copy = {};
            for (const attr in obj) {
                if (obj.hasOwnProperty(attr)) {
                    copy[attr] = DashGisUtil.clone(obj[attr]);
                }
            }
            return DashGisUtil.doReturn(copy);
        }
        throw new Error('Unable to clone object: type not supported');
    }

    static flatten(arr) {
        return arr.reduce((acc, val) => (Array.isArray(val) ? acc.concat(DashGisUtil.flatten(val)) : acc.concat(val)), []);
    }

    static uid() {
        const arr = new Uint8Array(6);
        const crypto = window.crypto || window['msCrypto'];
        crypto.getRandomValues(arr);
        return Array.from(arr, (dec) => ('0' + dec.toString(36)).substr(-2)).join('');
    }

    static dataUriToBlob(uri: string) {
        const byteString = atob(uri.split(',')[1]);
        const mimeString = uri.split(',')[0].split(':')[1].split(';')[0];
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const byteArray = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
        }

        return new Blob([arrayBuffer], { type: mimeString });
    }

    static compareRect(a: DOMRect, b: DOMRect): boolean {
        if (!(a && b)) {
            return false;
        }
        const props = ['x', 'y', 'width', 'height', 'top', 'right', 'bottom', 'left'];
        let equal = true;
        for (let i = 0; i < props.length; i++) {
            const prop = props[i];
            equal = equal && a[prop] && b[prop] && a[prop] === b[prop];
        }
        return equal;
    }

    /**
     * Determine if rect a intersects b, (and, of course, vice versa)
     * @param a DOMRect
     * @param b DOMRect
     */
    static rectsIntersect(a: Partial<DOMRect>, b: Partial<DOMRect>): boolean {
        if (!(a && b)) {
            return false;
        }

        return !(a.right < b.left || a.left > b.right || a.top > b.bottom || a.bottom < b.top);
    }

    /**
     * Determine if DOM element a is an ancestor of DOM element b
     * @param a Element
     * @param b Element
     */
    static isAncestor(a: Element, b: Element) {
        let parent = b.parentNode;
        while (parent && parent !== a) {
            parent = parent.parentNode;
        }
        return parent === a;
    }

    /**
     * Find the first occurence of [needle] in [haystack] and return a new string without [needle]
     * @param needle
     * @param haystack
     */
    static removeSubstring(needle: string, haystack: string) {
        const start = haystack.indexOf(needle);
        return haystack.substr(0, start) + haystack.substr(start + needle.length);
    }

    static buildRequestedRoute(targetUrl: string) {
        // only need a fake url base here since we are only using it for parsing.
        const urlObject = new URL(targetUrl, 'file://foo/');
        const path = urlObject.pathname;

        const queryParams: Params = {};
        for (const entry of urlObject.searchParams) {
            queryParams[entry[0]] = entry[1];
        }

        const requested: RequestedRoute = {
            url: path,
            queryParams,
        };
        return requested;
    }

    static buildPath = (route: ActivatedRouteSnapshot) => {
        let fullPath = '/';
        if (route.children.length === 0) {
            fullPath += route.url[0].path;
        } else {
            fullPath += route.url[0].path + DashGisUtil.buildPath(route.children[0]);
        }
        return fullPath;
    };


    private static doReturn<R>(val: R): R {
        DashGisUtil.cloneDepth--;
        return val as R;
    }
}
